'use client';

import { useState, useRef, useCallback } from 'react';
import Link from 'next/link';

export interface IndustryServiceTabItem {
  name: string;
  description: string;
  href: string;
}

export interface IndustryServiceTab {
  id: string;
  label: string;
  description?: string;
  services: IndustryServiceTabItem[];
}

interface IndustryServicesTabsProps {
  tabs: IndustryServiceTab[];
}

export default function IndustryServicesTabs({ tabs }: IndustryServicesTabsProps) {
  const filteredTabs = tabs.filter(tab => tab.services.length > 0);
  const [activeTab, setActiveTab] = useState(filteredTabs[0]?.id ?? '');

  const currentTab = filteredTabs.find(tab => tab.id === activeTab) ?? filteredTabs[0];

  const tablistRef = useRef<HTMLDivElement>(null);

  const handleTabKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      const currentIndex = filteredTabs.findIndex(tab => tab.id === activeTab);
      let nextIndex: number;
      if (e.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % filteredTabs.length;
      } else {
        nextIndex = (currentIndex - 1 + filteredTabs.length) % filteredTabs.length;
      }
      setActiveTab(filteredTabs[nextIndex].id);
      const buttons = tablistRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
      buttons?.[nextIndex]?.focus();
    },
    [activeTab, filteredTabs],
  );

  if (!currentTab) {
    return null;
  }

  return (
    <section className="relative rounded-3xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm md:p-10">
      <div ref={tablistRef} role="tablist" className="flex flex-wrap gap-3" onKeyDown={handleTabKeyDown}>
        {filteredTabs.map(tab => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`tabpanel-${tab.id}`}
            id={`tab-${tab.id}`}
            tabIndex={activeTab === tab.id ? 0 : -1}
            onClick={() => setActiveTab(tab.id)}
            className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2813e8] ${
              tab.id === activeTab
                ? 'border-[#2813e8] bg-[#2813e8] text-white shadow-md'
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-[#2813e8] hover:border-[#2813e8]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {currentTab.description && (
        <p className="mt-8 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
          {currentTab.description}
        </p>
      )}

      <div role="tabpanel" id={`tabpanel-${currentTab.id}`} aria-labelledby={`tab-${currentTab.id}`} className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {currentTab.services.map(service => (
          <div
            key={service.href}
            className="flex h-full flex-col rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-6 transition-colors hover:border-[#2813e8]"
          >
            <h3 className="text-lg font-semibold text-[#2813e8]">
              {service.name}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
              {service.description}
            </p>
            <Link
              href={service.href}
              className="mt-auto inline-flex items-center gap-1 text-sm font-semibold text-[#2813e8] transition-colors hover:text-[#1f10b5]"
            >
              Learn more
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01-.02-1.06L10.418 10 7.19 6.29a.75.75 0 011.08-1.04l3.75 3.9a.75.75 0 010 1.04l-3.75 3.9a.75.75 0 01-1.06.02z" clipRule="evenodd" />
              </svg>
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
}
